import { useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import {
  TabKicker,
  TabSectionTitle,
  HeroCard,
  ChartCard,
} from "./_shell";
import CountrySocieties from "./CountrySocieties";

/**
 * TabInternationalExtras — editorial split-hero.
 *   • Hero card (col-span 4): international share % + total students
 *   • Chart card (col-span 8): source-country bar strip
 *   • 3-card PSW pathway grid with hairline dividers
 *
 * Testids preserved: tab-international-extras, international-share-pct,
 * international-country-strip.
 */

function CountryStrip({ countries }) {
  if (!countries?.length) return null;
  const maxShare = Math.max(...countries.map((c) => c.share_pct || 0));
  return (
    <div className="space-y-2" data-testid="international-country-strip">
      {countries.map((c) => {
        const share = c.share_pct || 0;
        const barW = maxShare > 0 ? Math.max(2, (share / maxShare) * 100) : 0;
        return (
          <div key={c.country}
               className="grid grid-cols-[minmax(0,110px)_1fr_90px] items-center gap-4">
            <span className="font-sans text-sm text-[#002147] truncate">{c.country}</span>
            <div className="h-5 bg-[#002147]/[0.04] relative">
              <div className="absolute inset-y-0 left-0 bg-[#7C3AED] flex items-center justify-end pr-2"
                   style={{ width: `${barW}%` }}>
                {barW > 30 && (
                  <span className="text-white font-mono text-[9px] uppercase tracking-widest">
                    {share}%
                  </span>
                )}
              </div>
            </div>
            <span className="font-mono text-xs text-[#002147]/70 text-right tabular-nums">
              {c.students?.toLocaleString() || "—"}
              {share && barW <= 30 ? <span className="text-[#002147]/40 ml-1">· {share}%</span> : null}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default function TabInternationalExtras({ uni }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get(`/universities/${uni.id}/international-signals`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [uni.id]);

  if (!data) return null;
  const intl = data.international || {};
  const psw = data.psw || {};
  const hasCountries = intl.top_countries?.length > 0;
  const hasPsw = psw.years || psw.pathways || psw.sponsor || psw.deposit_refundable_on_visa_refusal != null;

  return (
    <section className="space-y-8 mb-8" data-testid="tab-international-extras">
      <div>
        <TabKicker>01 · International Cohort</TabKicker>
        <TabSectionTitle>The community you'd join</TabSectionTitle>
      </div>

      {hasCountries && (
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          <HeroCard
            className="md:col-span-4"
            kicker="International share"
            metric={intl.share_pct ? `${intl.share_pct}%` : "—"}
            subtitle={`${intl.total?.toLocaleString() || "—"} students`}
            prose="A proxy for classroom diversity and the diaspora networks you'd tap into."
          >
            <span className="sr-only" data-testid="international-share-pct">
              {intl.share_pct}
            </span>
          </HeroCard>
          <ChartCard
            className="md:col-span-8"
            title="Top source countries"
            legend={<span>Enrolled international undergraduates</span>}
          >
            <CountryStrip countries={intl.top_countries} />
          </ChartCard>
        </div>
      )}

      {hasPsw && (
        <div>
          <TabKicker>02 · Visa Pathway</TabKicker>
          <TabSectionTitle>After graduation</TabSectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#002147]/10 border border-[#002147]/10">
            <div className="bg-[#F3F1EC] p-6 flex flex-col">
              <span className="font-mono text-[10px] uppercase tracking-widest text-[#002147]/60">
                Graduate visa
              </span>
              <span className="font-serif text-4xl text-[#002147] mt-3">
                {psw.years ? `${psw.years} yrs` : "—"}
              </span>
              <span className="font-sans text-xs text-[#002147]/70 mt-2 leading-relaxed">
                Stay & work after graduation without sponsorship.
              </span>
            </div>
            {psw.deposit_refundable_on_visa_refusal != null && (
              <div className="bg-[#F3F1EC] p-6 flex flex-col">
                <span className="font-mono text-[10px] uppercase tracking-widest text-[#002147]/60">
                  Deposit refund
                </span>
                <span className="font-serif text-2xl text-[#002147] mt-3 inline-flex items-center gap-2">
                  {psw.deposit_refundable_on_visa_refusal
                    ? <><ShieldCheck className="h-6 w-6 text-emerald-700" /> Refundable</>
                    : "Not refundable"}
                </span>
                <span className="font-sans text-xs text-[#002147]/70 mt-2 leading-relaxed">
                  If your visa is refused.
                </span>
              </div>
            )}
            {psw.intl_office_quality != null && (
              <div className="bg-[#F3F1EC] p-6 flex flex-col">
                <span className="font-mono text-[10px] uppercase tracking-widest text-[#002147]/60">
                  Intl. office
                </span>
                <span className="font-serif text-4xl text-[#002147] mt-3">
                  {psw.intl_office_quality}
                  <span className="text-[#002147]/40 text-lg">/10</span>
                </span>
                <span className="font-sans text-xs text-[#002147]/70 mt-2 leading-relaxed">
                  Editorial score for visa & welcome support.
                </span>
              </div>
            )}
          </div>
          {psw.sponsor?.register && (
            <p className="mt-4 font-mono text-[10px] uppercase tracking-wider text-[#002147]/50">
              <span className="text-[#002147]/80 font-semibold">
                {psw.sponsor.authority || "Home Office"}
              </span>{" "}
              licensed sponsor · Ref {psw.sponsor.sponsor_ref || "—"} · listed on the {psw.sponsor.register}
            </p>
          )}
        </div>
      )}

      <CountrySocieties uniId={uni.id} uniName={uni.name} />
    </section>
  );
}
